import { USER_CONSTANTS } from '@core/constants/user.constants';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserRequest } from '@core/models/user.request.model';
import { LocalStorage } from '../local-storage/local-storage.service';
import { User } from '@core/models/user.model';
import { ApiResponse } from '@core/models/common/api-response.model';
import { ForgetPasswordRequest } from '@modules/auth/models/forget-password-request.model';
import { ResetPasswordRequest } from '@modules/auth/models/reset-password-request.model';
import { Provider } from '@modules/auth/models/provider.model';
import { RegistrationRequest } from '@modules/auth/models/registration-request.model';
import { Course } from '@core/models/course.model';
import { Conference } from '@core/models/conference.model';
import { UserCertificatesResponse } from '@core/models/user-certificate-response.model';
import { ChangePasswordRequest } from '@modules/my-account/models/change-password-request.model';
import { UpdateUserRequest } from '@modules/my-account/models/update-user-request.model';
import { QuickRegistrationRequest } from '@modules/auth/models/quick-registration-request.model';
import { APP_CONFIG } from '@core/config/app-config.token';
import { AppConfig } from '@core/config/app-config.model';
import { PendingSessionSelectionService } from '@core/services/pending-session-selection/pending-session-selection.service';

interface StoredAuth {
  apiToken?: string;
  user?: User;
  redirectUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isLoggedInSubject!: BehaviorSubject<boolean>;

  private loginUrl!: string;
  private getUserDataUrl!: string;
  private registerUrl!: string;
  private quickRegisterUrl!: string;
  private forgetPasswordUrl!: string;
  private resetPasswordUrl!: string;
  private npiProviderUrl!: string;
  private courseUrl!: string;
  private conferenceUrl!: string;
  private certificateUrl!: string;
  private updateUserUrl!: string;
  private aliasLoginUrl!: string;
  private aliasUpdateProfileUrl!: string;

  private readonly localStorageKey = USER_CONSTANTS.AUTH_STORAGE_KEY;

  constructor(
    private http: HttpClient,
    private localStore: LocalStorage,
    @Inject(APP_CONFIG) private readonly cfg: AppConfig,
    private readonly pendingSessionSelectionService: PendingSessionSelectionService
  ) {
    this.removeLegacyStorage();
    this.isLoggedInSubject = new BehaviorSubject<boolean>(this.isAuthenticated());
    this.loginUrl = `${this.cfg.serviceApiUrl}/login`;
    this.getUserDataUrl = `${this.cfg.serviceApiUrl}/user/account`;
    this.registerUrl = `${this.cfg.serviceApiUrl}/register`;
    this.quickRegisterUrl = `${this.cfg.serviceApiUrl}/quick-register`;
    this.forgetPasswordUrl = `${this.cfg.serviceApiUrl}/forget-password`;
    this.resetPasswordUrl = `${this.cfg.serviceApiUrl}/reset-password`;
    this.npiProviderUrl = `${this.cfg.npiApiUrl}providers/`;
    this.courseUrl = `${this.cfg.serviceApiUrl}/user/courses`;
    this.conferenceUrl = `${this.cfg.serviceApiUrl}/user/conferences`;
    this.certificateUrl = `${this.cfg.serviceApiUrl}/user/certificates`;
    this.updateUserUrl = `${this.cfg.serviceApiUrl}/user/update`;
    this.aliasLoginUrl = `${this.cfg.serviceApiUrl}/alias/login`;
    this.aliasUpdateProfileUrl = `${this.cfg.serviceApiUrl}/alias/update-profile`;
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.isLoggedInSubject.asObservable();
  }

  login(request: UserRequest): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(this.loginUrl, request);
  }

  register(request: RegistrationRequest): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(this.registerUrl, request);
  }

  quickRegister(request: QuickRegistrationRequest): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(this.quickRegisterUrl, request);
  }

  logout(): void {
    this.localStore.remove(this.localStorageKey);
    this.removeLegacyStorage();
    this.pendingSessionSelectionService.clear();
    this.isLoggedInSubject.next(false);
  }

  storeUserToken(userResponse: ApiResponse<User>): void {
    if (!userResponse.data) {
      throw new Error(USER_CONSTANTS.USER_RESPONSE_NULL);
    }

    // Fresh server payload is authoritative; only the pending redirect survives from before login.
    const userData: StoredAuth = {
      apiToken: userResponse.data.token,
      user: userResponse.data,
    };
    const pendingRedirect = this.readStoredAuth().redirectUrl;
    if (pendingRedirect) {
      userData.redirectUrl = pendingRedirect;
    }
    this.writeStoredAuth(userData);
    this.isLoggedInSubject.next(true);
  }

  getToken(): string {
    return this.readStoredAuth().apiToken || '';
  }

  getUser(): User | null {
    return this.readStoredAuth().user || null;
  }

  getUserData(): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(this.getUserDataUrl);
  }

  public isAuthenticated(): boolean {
    return !!this.getToken();
  }

  forgetPassword(request: ForgetPasswordRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(this.forgetPasswordUrl, request);
  }

  resetPassword(request: ResetPasswordRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(this.resetPasswordUrl, request);
  }

  getProviderByNpi(npi: number): Observable<ApiResponse<Provider>> {
    return this.http.get<ApiResponse<Provider>>(`${this.npiProviderUrl}${npi}`);
  }

  getUserCourses(): Observable<ApiResponse<Course[]>> {
    return this.http.get<ApiResponse<Course[]>>(`${this.courseUrl}`);
  }

  getUserConferences(): Observable<ApiResponse<Conference[]>> {
    return this.http.get<ApiResponse<Conference[]>>(`${this.conferenceUrl}`);
  }

  getUserCertificates(): Observable<ApiResponse<UserCertificatesResponse>> {
    return this.http.get<ApiResponse<UserCertificatesResponse>>(`${this.certificateUrl}`);
  }

  changePassword(request: ChangePasswordRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(
      `${this.cfg.serviceApiUrl}/user/change-password`,
      request
    );
  }

  updateUser(data: UpdateUserRequest): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(this.updateUserUrl, data);
  }

  checkUserByEmail(email: string): Observable<ApiResponse<User>> {
    const url = `${this.cfg.serviceApiUrl}/check-email`;
    return this.http.get<ApiResponse<User>>(url, {
      params: { email },
    });
  }

  setRedirectUrl(url: string): void {
    if (!this.isValidInternalUrl(url)) {
      this.clearRedirectUrl();
      return;
    }
    this.writeStoredAuth({ ...this.readStoredAuth(), redirectUrl: url });
  }

  getRedirectUrl(): string | null {
    const url = this.readStoredAuth().redirectUrl ?? null;
    if (!this.isValidInternalUrl(url)) {
      this.clearRedirectUrl();
      return null;
    }
    return url;
  }

  private isValidInternalUrl(url: string | null): url is string {
    return !!url && url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');
  }

  clearRedirectUrl(): void {
    const { redirectUrl, ...rest } = this.readStoredAuth();
    if (redirectUrl === undefined) {
      return;
    }
    if (Object.keys(rest).length === 0) {
      this.localStore.remove(this.localStorageKey);
      return;
    }
    this.writeStoredAuth(rest);
  }

  private readStoredAuth(): StoredAuth {
    const raw = this.localStore.get<StoredAuth | string>(this.localStorageKey);
    try {
      const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return parsed && typeof parsed === 'object' ? (parsed as StoredAuth) : {};
    } catch {
      return {};
    }
  }

  private writeStoredAuth(data: StoredAuth): void {
    this.localStore.store(this.localStorageKey, JSON.stringify(data));
  }

  private removeLegacyStorage(): void {
    this.localStore.remove(USER_CONSTANTS.LEGACY_AUTH_STORAGE_KEY);
    this.localStore.remove(USER_CONSTANTS.LEGACY_REDIRECT_STORAGE_KEY);
  }

  updateUserEmail(email: string, pushLiveUuid: string) {
    const url = `${this.cfg.serviceApiUrl}/update-email`;

    return this.http.post(url, {
      email,
      push_live_uuid: pushLiveUuid,
    });
  }

  aliasLogin(request: UserRequest): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(this.aliasLoginUrl, request);
  }

  aliasUpdateProfile(data: UpdateUserRequest): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(this.aliasUpdateProfileUrl, data);
  }

  checkAliasUserByEmail(email: string): Observable<ApiResponse<User>> {
    const url = `${this.cfg.serviceApiUrl}/alias/check-email`;
    return this.http.get<ApiResponse<User>>(url, {
      params: { email },
    });
  }
}
